import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector } from "react-redux";
import OT from '@opentok/client';
import { Capacitor } from '@capacitor/core'; // ✅ AJOUT

import './ModernHairCheck.css';

export default function ModernHairCheck({ joinCall, cancelCall, vonageConfig, session }) {
  const { user } = useSelector(state => state.user);
  
  const isIOS = (() => {
    // Capacitor iOS (app native)
    if (Capacitor.getPlatform() === 'ios') return true;
    
    // Safari iOS / iPadOS
    const ua = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(ua);
    
    // iPad sur iPadOS 13+ peut se faire passer pour un Mac
    const isIPadOS = navigator.maxTouchPoints > 1 && /macintosh/.test(ua);
    
    return isIOSDevice || isIPadOS;
  })();

  const [publisher, setPublisher] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [getUserMediaError, setGetUserMediaError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const [devices, setDevices] = useState({
    microphones: [],
    speakers: [],
    cameras: [],
    selectedMicrophone: null,
    selectedSpeaker: null,
    selectedCamera: null
  });
  
  const [audioLevel, setAudioLevel] = useState(0);
  
  const publisherContainer = useRef();
  const isComponentMounted = useRef(true);
  const audioLevelInterval = useRef();
  const isVideoAttached = useRef(false);
  // Ref toujours à jour pour le cleanup au démontage — évite la stale closure
  // du useEffect([], () => cleanupPreview()) qui capturerait publisher=null.
  const publisherRef = useRef(null);

  useEffect(() => {
    isComponentMounted.current = true;
    return () => {
      isComponentMounted.current = false;
      if (audioLevelInterval.current) {
        clearInterval(audioLevelInterval.current);
      }
    };
  }, []);

  useEffect(() => {
    if (vonageConfig && !publisher && !isLoading && !getUserMediaError) {
      initializePreview();
    }
  }, [vonageConfig]);

  const initializePreview = async () => {
    if (!isComponentMounted.current || isLoading) return;
    
    setIsLoading(true);
    setGetUserMediaError(false);
    setErrorMessage('');

    try {
      console.log("Initialisation du preview caméra...");
      
      await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      
      if (!isComponentMounted.current) return;

      const pub = OT.initPublisher(undefined, {
        insertMode: 'replace',
        width: '100%',
        height: '100%',
        name: `${user?.name || 'User'}_Preview`,
        publishAudio: false,
        publishVideo: true,
        style: {
          buttonDisplayMode: 'off'
        }
      }, (error) => {
        if (!isComponentMounted.current) return;
        
        if (error) {
          console.error("Erreur création publisher:", error);
          setErrorMessage(`Erreur d'initialisation: ${error.message}`);
          setGetUserMediaError(true);
          setIsLoading(false);
        } else {
          console.log("Publisher preview créé avec succès");
          attachVideoElement(pub);
        }
      });

    } catch (error) {
      if (!isComponentMounted.current) return;
      
      console.error("Erreur accès médias:", error);
      
      if (error.name === 'NotAllowedError') {
        setErrorMessage("Accès refusé. Veuillez autoriser l'accès à votre caméra et microphone.");
      } else if (error.name === 'NotFoundError') {
        setErrorMessage("Aucune caméra ou microphone détecté sur votre appareil.");
      } else {
        setErrorMessage(`Erreur d'accès aux médias: ${error.message}`);
      }
      
      setGetUserMediaError(true);
      setIsLoading(false);
    }
  };

  const attachVideoElement = useCallback(async (pub) => {
    if (!pub || !publisherContainer.current || isVideoAttached.current) return;

    try {
      publisherContainer.current.innerHTML = '';
      
      if (pub.element) {
        publisherContainer.current.appendChild(pub.element);
        isVideoAttached.current = true;
        console.log("Élément vidéo attaché");
      }
      
      setPublisher(pub);
      publisherRef.current = pub;

      await loadAvailableDevices();
      
      startAudioLevelMonitoring(pub);
      
      setIsReady(true);
      setIsLoading(false);
      
    } catch (error) {
      console.error("Erreur attachement vidéo:", error);
      setErrorMessage("Erreur lors de l'affichage de la vidéo");
      setIsLoading(false);
    }
  }, []);

  const loadAvailableDevices = async () => {
    try {
      const deviceInfos = await navigator.mediaDevices.enumerateDevices();
      
      if (!isComponentMounted.current) return;
      
      const microphones = deviceInfos.filter(device => device.kind === 'audioinput');
      const speakers = deviceInfos.filter(device => device.kind === 'audiooutput');
      const cameras = deviceInfos.filter(device => device.kind === 'videoinput');

      console.log("Devices détectés:", { microphones: microphones.length, speakers: speakers.length, cameras: cameras.length });

      setDevices({
        microphones,
        speakers,
        cameras,
        selectedMicrophone: microphones[0]?.deviceId || null,
        selectedSpeaker: speakers[0]?.deviceId || null,
        selectedCamera: cameras[0]?.deviceId || null
      });

    } catch (error) {
      console.error("Erreur énumération devices:", error);
    }
  };

  const startAudioLevelMonitoring = useCallback((pub) => {
    if (audioLevelInterval.current) {
      clearInterval(audioLevelInterval.current);
    }

    audioLevelInterval.current = setInterval(() => {
      if (!isComponentMounted.current || !pub) return;
      
      try {
        const randomLevel = Math.random() * 30 + (Math.random() > 0.7 ? 20 : 0);
        setAudioLevel(Math.min(randomLevel, 100));
      } catch (error) {
        // Ignore
      }
    }, 300);
  }, []);

  const handleCameraChange = async (event) => {
    const deviceId = event.target.value;
    setDevices(prev => ({ ...prev, selectedCamera: deviceId }));

    if (publisher && isReady && deviceId) {
      try {
        console.log("Changement de caméra:", deviceId);
        await publisher.setVideoSource(deviceId);
      } catch (error) {
        console.error("Erreur changement caméra:", error);
        setErrorMessage("Erreur lors du changement de caméra");
      }
    }
  };

  const handleMicrophoneChange = async (event) => {
    const deviceId = event.target.value;
    setDevices(prev => ({ ...prev, selectedMicrophone: deviceId }));

    if (publisher && isReady && deviceId) {
      try {
        console.log("Changement de microphone:", deviceId);
        await publisher.setAudioSource(deviceId);
      } catch (error) {
        console.error("Erreur changement microphone:", error);
        setErrorMessage("Erreur lors du changement de microphone");
      }
    }
  };

  const handleSpeakerChange = (event) => {
    const deviceId = event.target.value;
    setDevices(prev => ({ ...prev, selectedSpeaker: deviceId }));
    console.log("Haut-parleur sélectionné:", deviceId);
  };

  const handleJoinCall = (event) => {
    event.preventDefault();
    
    if (!isReady) return;
    
    console.log("Transition vers l'appel avec les devices:", devices);
    
    cleanupPreview();
    
    joinCall({
      selectedCamera: devices.selectedCamera,
      selectedMicrophone: devices.selectedMicrophone,
      selectedSpeaker: devices.selectedSpeaker
    });
  };

  const handleCancel = () => {
    cleanupPreview();
    cancelCall();
  };

  const handleRetry = () => {
    cleanupPreview();
    
    setGetUserMediaError(false);
    setErrorMessage('');
    setIsReady(false);
    setPublisher(null);
    
    setTimeout(() => {
      initializePreview();
    }, 500);
  };

  const cleanupPreview = useCallback(() => {
    console.log("Nettoyage du preview...");
    
    if (audioLevelInterval.current) {
      clearInterval(audioLevelInterval.current);
      audioLevelInterval.current = null;
    }
    
    isVideoAttached.current = false;
    
    // ✅ AJOUT : Arrêter les tracks média du preview
    if (publisher) {
      try {
        const videoTrack = publisher.getVideoSource();
        const audioTrack = publisher.getAudioSource();
        
        if (videoTrack && videoTrack.stop) {
          videoTrack.stop();
          console.log("Preview - Track vidéo arrêté");
        }
        
        if (audioTrack && audioTrack.stop) {
          audioTrack.stop();
          console.log("Preview - Track audio arrêté");
        }
      } catch (error) {
        console.error("Erreur arrêt tracks preview:", error);
      }
    }
    
    if (publisherContainer.current) {
      publisherContainer.current.innerHTML = '';
    }
    
    if (publisher) {
      try {
        publisher.destroy();
        console.log("Publisher preview détruit");
      } catch (error) {
        console.log("Erreur destruction publisher:", error);
      }
    }
    
    setPublisher(null);
    publisherRef.current = null;
    setIsReady(false);
    setAudioLevel(0);
  }, [publisher]);

  useEffect(() => {
    return () => {
      // Arrêt forcé de tous les tracks via publisherRef (évite la stale closure
      // de cleanupPreview qui aurait capturé publisher=null au montage).
      if (audioLevelInterval.current) {
        clearInterval(audioLevelInterval.current);
      }
      const pub = publisherRef.current;
      if (pub) {
        try {
          // Arrêt via stream.getTracks() — couvre tous les tracks actifs
          pub.stream?.getTracks()?.forEach(t => t.stop());
        } catch (e) { /* ignore */ }
        try { pub.getVideoSource()?.stop(); } catch (e) { /* ignore */ }
        try { pub.getAudioSource()?.stop(); } catch (e) { /* ignore */ }
        try { pub.destroy(); } catch (e) { /* ignore */ }
        publisherRef.current = null;
      }
      if (publisherRef.current === null && isVideoAttached.current) {
        isVideoAttached.current = false;
      }
    };
  }, []);

  if (getUserMediaError) {
    return (
      <div className="modern-hair-check">
        <div className="hair-check-container">
          <div className="error-container">
            <h2 className="error-title">Erreur d'accès aux médias</h2>
            <p className="error-message">
              {errorMessage || "Impossible d'accéder à votre caméra ou microphone. Veuillez vérifier les permissions de votre navigateur."}
            </p>
            <div className="error-actions">
              <button className="error-button" onClick={handleRetry}>
                Réessayer
              </button>
              <button className="error-button" onClick={handleCancel}>
                Retour
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-hair-check">
      <div className="hair-check-container">
        
        <div className="hair-check-header">
          <h1 className="hair-check-title">Test de votre équipement</h1>
          <p className="hair-check-subtitle">
            Vérifiez votre caméra et microphone avant de rejoindre la consultation
          </p>
        </div>

        <div className={`video-preview-container ${isReady ? 'ready' : isLoading ? 'loading' : ''}`}>
          <div ref={publisherContainer} className="video-preview" />
          
          {!isReady && (
            <div className="preview-placeholder">
              {isLoading ? (
                <>
                  <div className="loading-spinner"></div>
                  <div className="text">Initialisation de la caméra...</div>
                </>
              ) : (
                <>
                  <div className="icon">📷</div>
                  <div className="text">En attente d'initialisation</div>
                </>
              )}
            </div>
          )}
          
          {isReady && (
            <div className="status-indicator status-ready">
              Prêt
            </div>
          )}
          {isLoading && (
            <div className="status-indicator status-loading">
              Chargement
            </div>
          )}
        </div>

        <form onSubmit={handleJoinCall} className="devices-section">
          <div className="devices-grid">
            
            <div className="device-selector">
              <label className="device-label">
                <span className="device-icon">📷</span>
                Caméra
              </label>
              <select 
                className="device-select" 
                value={devices.selectedCamera || ''} 
                onChange={handleCameraChange}
                disabled={!isReady}
              >
                {devices.cameras.map((camera) => (
                  <option key={camera.deviceId} value={camera.deviceId}>
                    {camera.label || `Caméra ${camera.deviceId.substring(0, 8)}...`}
                  </option>
                ))}
              </select>
            </div>

            <div className="device-selector">
              <label className="device-label">
                <span className="device-icon">🎤</span>
                Microphone
              </label>
              <select 
                className="device-select" 
                value={devices.selectedMicrophone || ''} 
                onChange={handleMicrophoneChange}
                disabled={!isReady}
              >
                {devices.microphones.map((mic) => (
                  <option key={mic.deviceId} value={mic.deviceId}>
                    {mic.label || `Microphone ${mic.deviceId.substring(0, 8)}...`}
                  </option>
                ))}
              </select>
              
              {isReady && (
                <div className="audio-level-container">
                  <span className="audio-level-label">Niveau audio</span>
                  <div className="audio-level-bar">
                    <div 
                      className="audio-level-fill" 
                      style={{ width: `${audioLevel}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

          {/* Haut-parleurs - MASQUÉ SUR iOS */}
          {!isIOS && (
            <div className="device-selector">
              <label className="device-label">
                <span className="device-icon">🔊</span>
                Haut-parleurs
              </label>
              <select 
                className="device-select" 
                value={devices.selectedSpeaker || ''} 
                onChange={handleSpeakerChange}
                disabled={!isReady}
              >
                {devices.speakers.map((speaker) => (
                  <option key={speaker.deviceId} value={speaker.deviceId}>
                    {speaker.label || `Haut-parleur ${speaker.deviceId.substring(0, 8)}...`}
                  </option>
                ))}
              </select>
            </div>
          )}
          </div>

          <div className="actions-section">
            <button 
              type="button" 
              className="action-button secondary-button" 
              onClick={handleCancel}
            >
              Annuler
            </button>
            
            <button 
              type="submit" 
              className="action-button primary-button" 
              disabled={!isReady}
            >
              {isReady ? 'Rejoindre la consultation' : 'Chargement...'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}